/**
 * levelgen/landmarks.ts — The places the run is *about*, and the planting.
 *
 * A walk **starts and ends at home**: out of the front door, onto some grass so
 * the dog can go, over to the bin with the bag, and back. So there are two
 * fixed points rather than three — home and the bin — plus a **green**, which
 * is now as load-bearing as either of them because a dog will only do its
 * business on grass (see `systems/grass.ts`).
 *
 * Where those three go is the single biggest decision the generator makes: it
 * fixes how far the errand is, how many carriageways it puts the player across,
 * and therefore most of what the difficulty rating ends up measuring.
 *
 * ## Why they are chosen together rather than one at a time
 *
 * Placing each in turn — bin somewhere sensible, then home somewhere sensible —
 * gives an errand of whatever length falls out, and the length is the point. So
 * instead every plausible spot for each is enumerated first, and the
 * *combination* nearest the difficulty budget's target journey wins. That is a
 * small search rather than a clever one, which is the right shape here: the
 * candidate lists are short, and a scored search is far easier to reason about
 * than a placement heuristic that tries to be right first time.
 *
 * ## Where a landmark may stand
 *
 * On the open ground inside a block — never on the footway. The pavement is the
 * walkable band the whole game is played on, and standing the bin in the middle
 * of it would pinch the route past it (`OBJECTIVE.BIN_RADIUS` is bigger than
 * the footway is wide). `systems/yards.ts` → `freeRects` already knows how to
 * work out what ground inside a block is free, so that is what is asked.
 *
 * A bin gets two rules on top of that. It stands **against a frontage**, a pace
 * off the kerb, on the paved apron between the pavement and the houses: a bin
 * is street furniture, and one out in the middle of a block is a bin in the
 * middle of nowhere — an object the player can only find by walking over it.
 * And it stands **well clear of every green** (`binGreenClearance`), which is
 * the rule that keeps the back half of the errand a walk; it used to be the
 * exact opposite, and the note on that function is the whole argument.
 * `placeBins` adds the rest of the town's bins under both.
 */
import {
  CAMERA,
  DOG,
  LEASH,
  OBJECTIVE,
  PLAYER,
  PAVEMENT,
  ROADS,
  TREES,
} from '../../config/constants';
import { homeDoorstep } from '../home';
import { freeRects } from '../yards';
import { worthPlanting } from './surround';
import { footwayBands } from '../pavement';
import { carriagewayRects } from '../roads';
import { apparentCanopyRect, canopyRect } from '../trees';
import {
  faceGaps,
  inflateRect,
  pointInAnyRect,
  pointInRect,
  pointRect,
  rectContains,
  rectGap,
  rectsOverlap,
  type Facing,
  type Rect,
} from '../level';
import type { LevelBudget } from './difficulty';
import type { Rng } from './rng';
import type { Point, TreeSpec } from './types';
import type { StreetPlan } from './town';
import type { TreeKind } from '../../types';

/** The fixed points of a run, and the ground they claim. */
export interface Landmarks {
  /**
   * The bin the errand is measured to — the one chosen together with home and
   * the green so the round trip lands on the budget's target length.
   *
   * The level ships with more of them (`placeBins`), but this is the one that
   * decided how long the walk is, and the extras are kept at roughly its own
   * distance from the front door so that stays true.
   */
  bin: Point;
  home: Rect;
  /**
   * Where the walker starts — the doormat of the house on the home plot,
   * always (`systems/home.ts` → `homeDoorstep`). A walk is a round trip from
   * the front door, so there is no separate start to place.
   */
  start: Point;
  /**
   * The town's greens, all of them reserved before a single house goes up.
   * `greens[0]` is the guaranteed one — the dog will only go on grass, so it is
   * the one park a level cannot do without, and it is chosen together with home
   * and the bin because how far it is decides how much of a detour the dog's
   * business puts on the walk.
   *
   * The rest used to be found afterwards, in `placeParks`, out of whatever
   * ground the terraces happened to leave over. That is what a leftover looks
   * like: 130 units across, one tree on it, and nothing else — the "small patch
   * of empty grass" a green is not supposed to be. Ground for a park is now
   * claimed in the same breath as the ground for home, which is the only way to
   * get a rectangle big enough to put a fountain and a path in.
   */
  greens: Rect[];
  /**
   * Ground the town may not build on. Handed to `placePlots`, which is what
   * makes the reservation mean anything.
   */
  reserved: Rect[];
}

/** Clear ground a landmark needs around it, beyond its own footprint. */
const LANDMARK_MARGIN = 12;
/** Nothing interesting should be within this of anything else interesting. */
const LANDMARK_SEPARATION = 170;
/**
 * How much clear ground there has to be between the home plot and the nearest
 * grass, as a fraction of the playable rectangle's own diagonal — with
 * `GREEN_MIN_SEPARATION` as the floor for the smallest towns.
 *
 * The rule used to be 150 units centre-to-centre, and *minimising* that
 * distance was part of the score. Between them those two put a green against
 * the garden fence on essentially every seed: the search took the nearest legal
 * patch of grass every time, so the first thing a player saw on stepping out of
 * the front door was the park they were supposed to go looking for. Getting the
 * dog somewhere it will go is the first half of the walk, and a green on the
 * doorstep deletes it.
 *
 * Measured against the map rather than fixed, so it means the same thing on a
 * small easy town and a sprawling very-hard one; and **edge to edge**, because
 * a big green whose centre is a street away can still have a corner over the
 * garden fence.
 */
const GREEN_SEPARATION_FRACTION = 0.16;
/** Floor under the above, for towns whose diagonal is short. */
const GREEN_MIN_SEPARATION = 220;

/**
 * How much clear ground the nearest grass has to leave around the home plot on
 * this map.
 *
 * Exported because `validate.ts` re-derives the same rule from the finished
 * level — the placer's job is to produce a green a walk away, and the
 * validator's is to refuse to believe it. It applies to **every** park, not
 * just the one the landmarks chose: `placeParks` fills the leftover ground
 * afterwards, and a spare green laid behind the house would put the walk back
 * on the doorstep by another route.
 */
export function greenSeparation(bounds: Rect): number {
  return Math.max(GREEN_MIN_SEPARATION, Math.hypot(bounds.w, bounds.h) * GREEN_SEPARATION_FRACTION);
}

/**
 * How far a bin stands back from the frontage it is placed against.
 *
 * Its own half-width plus a pace of paving, so the body of the bin is clear of
 * the footway (which `validate.ts` → `bin-on-footway` insists on) while still
 * reading as standing at the kerb rather than in somebody's front garden.
 */
const BIN_SETBACK = OBJECTIVE.BIN.SIZE / 2 + 12;
/**
 * How far apart two bins must be.
 *
 * The point of a second bin is to be somewhere the first one isn't: two on the
 * same corner are one bin with a spare, and the walk is no less of a hunt.
 */
const BIN_SEPARATION = 260;
/**
 * How much clear town there has to be between any bin and any green, as a
 * fraction of the playable rectangle's own diagonal — with
 * `BIN_GREEN_MIN_CLEARANCE` as the floor for the smallest towns.
 *
 * **A bin used to be allowed to stand beside a park, and was actively preferred
 * there** — `parkSideSpots` offered the paved apron off each side of a green
 * and a `PARK_SIDE_BONUS` worth about a street's distance pushed the placer
 * toward it, on the reasoning that the green is where the bag gets filled so
 * the bin should be where the bag is. That is exactly what makes it not a walk.
 * The bag is filled at the most predictable moment of the run; if a bin is
 * already in shot when it happens, the back half of the errand is three paces
 * and the town might as well not be there.
 *
 * So the rule is inverted: a green and a bin are never neighbours. Scaled
 * against the map rather than fixed, and deliberately the same shape (and the
 * same fraction) as `GREEN_SEPARATION_FRACTION` — this is that rule pointed at
 * the other end of the walk. Home has to be a walk from the grass so that
 * getting the dog somewhere it will go is a real leg of the run; the grass has
 * to be a walk from a bin for the same reason, at the other end. One number for
 * one idea: **no two things the errand sends you between are ever in sight of
 * each other.**
 *
 * The floor is what the smallest towns get, and it is sized off the screen:
 * the camera shows about `CAMERA.DISTANCE × tan(hFOV/2)` of ground to either
 * side of the walker, so a bin past that is off the edge of the frame from the
 * green and has to be gone looking for rather than merely walked to.
 *
 * Measured **edge to edge**, like every other separation here: a big green
 * whose centre is a street away can still have its near corner at the bin.
 *
 * Enforced in three places, because three different stages can put the two
 * together: `placeLandmarks` (the errand bin and the greens are chosen in the
 * same search), `generate.ts` (the errand bin's reservation, which is what
 * keeps `placeParks` off it), and `placeBins` (the extras, chosen last against
 * the finished parks). `validate.ts` → `bin-beside-the-grass` then refuses to
 * believe any of them.
 */
const BIN_GREEN_FRACTION = GREEN_SEPARATION_FRACTION;
/**
 * Floor under the above.
 *
 * Under the camera's own sideways reach rather than over it — the hand-authored
 * town is 960 × 640, and a green and a bin genuinely cannot be a full screen
 * apart on a map that size without one of them leaving it. This is as much
 * clear ground as that town has to give, and on anything the generator builds
 * the fraction above is what binds.
 */
const BIN_GREEN_MIN_CLEARANCE = 300;

/**
 * What the rule costs, measured over 160 generated towns, because it is a real
 * cost and the next person here should not have to rediscover it.
 *
 * Bins per town fell from 2.59 to about 2.1, and the share of towns shipping a
 * *single* bin rose from 14% to about a third. That is the same "find one
 * object on a map of terraces" the multi-bin work in `placeBins` set out to
 * kill, so it is a trade rather than a free win: the errand is a genuine hunt
 * again, and on a third of towns it is a hunt for exactly one thing.
 *
 * The cause is not this rule alone. Of the ~35 candidate spots a town offers,
 * `EXTRA_BIN_HOME_FLOOR` already rejected ~26 before any of this; the greens
 * used to supply four legal candidates apiece (`parkSideSpots`), which is what
 * quietly kept the pool full. Sampling the frontage gaps harder (`alongFace`)
 * buys a little of it back. If more is wanted, the home floor is the knob with
 * the slack in it — but that one decides how long the errand is, so moving it
 * is a difficulty decision rather than a tidy-up.
 */

/**
 * How much clear town there has to be between a bin and the nearest grass on
 * this map.
 *
 * Exported for the same reason `greenSeparation` is: three placers and the
 * validator all have to mean the same thing by it.
 */
export function binGreenClearance(bounds: Rect): number {
  return Math.max(BIN_GREEN_MIN_CLEARANCE, Math.hypot(bounds.w, bounds.h) * BIN_GREEN_FRACTION);
}
/**
 * How much nearer home than the errand bin an extra bin may be.
 *
 * The whole difficulty budget flows from where `placeLandmarks` put the errand
 * bin — the round trip to it is what `journeyTarget` aims at and what
 * `rateLevel` measures. A player walks to the *nearest* bin, so an extra one
 * dropped between home and that bin would silently shorten the walk the level
 * was rated for. Holding the extras at (very nearly) the same distance means
 * more bins make the town more legible without making the errand shorter.
 *
 * Not 1. At exactly the errand bin's distance the extras have to stand on a
 * circle, and on a third of the towns tried there was nowhere on that circle to
 * put one — the level shipped with the single bin this was all meant to fix. A
 * fifth of the errand is the slack that buys back those towns; it costs at most
 * that much off the *shortest* way to do the errand, which is well inside the
 * travel the tuner has to correct with (see `generate.ts` → `tuneToBand`), and
 * `rateLevel` measures whatever it actually comes to rather than assuming.
 */
const EXTRA_BIN_HOME_FLOOR = 0.8;
/**
 * How far the walker's start sits from the dog's — the dog spawns at heel,
 * inside the leash's slack length, so the walk doesn't open with a taut lead.
 * Mirrors `Game`'s constructor, and the clear ground the start needs is sized
 * to hold both.
 */
const DOG_OFFSET = LEASH.REST_LENGTH * 0.8;

/**
 * Daylight a canopy leaves around a footway, on top of simply not overlapping
 * it. Bare non-overlap is too weak: a tree can miss by a fraction of a unit and
 * still look like it's leaning over the pavement. Matches the margin
 * `treeClearance.test.ts` holds the authored town to.
 */
const FOOTWAY_MARGIN = 8;

/** Clear ground the bin claims, so there's room to walk up to it. */
const BIN_FOOTPRINT = { w: 100, h: 100 };
/**
 * The guaranteed green.
 *
 * Big enough to be worth walking to and to get the whole dog onto with room to
 * spare — `GRASS_MARGIN` is subtracted from every side before a dog counts as
 * being on it, so a token patch would be a park you can't use.
 *
 * It is also the *park* of a level that only has room for one, which is what
 * this size is really about. At 150 × 140 it was a patch of grass with a tree
 * on it; a green wants room for the path round the inside, a fountain in the
 * middle of it and a bench to sit the walk out on (`systems/parkFurniture.ts`),
 * and none of that fits on a lawn. Never smaller than `PARK_MIN`, which is the
 * floor every other green on the level is held to.
 */
const GREEN_FOOTPRINT = { w: 210, h: 185 };

/**
 * Choose home, the bin and the green — **before** the town is built around them.
 *
 * This order was arrived at the other way round and it did not work. Placing
 * the houses first and then looking for gaps big enough for a 120×120 home pad
 * fails for most seeds: a developed block simply doesn't have one, so nine
 * levels in ten came back with nowhere to live and the seed was thrown away.
 *
 * Reserving first inverts that. The three are chosen from the empty block grid,
 * where there is obviously room, and the reservation is handed to `placePlots`
 * as ground it may not build on. It is also how a level designer would go about
 * it — decide what the walk is, then fill in the town around it.
 *
 * The **walker starts at home**, so there is no third point to search for; what
 * the search balances instead is how far the bin is (the errand) and how far
 * the grass is (the detour the dog drags you on before you can even start it).
 *
 * Returns `null` only when the blocks are genuinely too small to hold the three
 * of them far enough apart, which is the generator's cue to try another seed.
 */
export function placeLandmarks(rng: Rng, plan: StreetPlan, budget: LevelBudget): Landmarks | null {
  // Each needs a different amount of elbow room, so each gets its own
  // shortlist. Capped: the search below is cubic in these lengths, and a
  // hundred candidates apiece is a million combinations for no better an answer
  // than a couple of dozen gives.
  // Only the blocks the walk can actually reach. A landmark in the surround is
  // one the player is clamped out of, so an errand aimed at it is unfinishable —
  // and the reachability check would reject the seed only *after* a whole town
  // had been built around a bin nobody can get to.
  const inPlay = plan.blocks.filter((b) => rectContains(plan.bounds, b));
  const footways = footwayBands(plan.world, plan.roads, ROADS.WIDTH, PAVEMENT.WIDTH);

  const homeSpots = shortlistHomeSpots(rng, plan, inPlay, OBJECTIVE.HOME.WIDTH, OBJECTIVE.HOME.HEIGHT, 4);
  const binSpots = kerbsideSpots(rng, inPlay, footways, 3);
  const greenSpots = shortlist(rng, inPlay, GREEN_FOOTPRINT.w, GREEN_FOOTPRINT.h, 5);
  if (!homeSpots.length || !binSpots.length || !greenSpots.length) return null;

  // How much clear ground the grass has to leave around the home plot, and
  // around the bin, on this map. Read once each: they are properties of the
  // town, not of the combination being scored.
  const greenGap = greenSeparation(plan.bounds);
  const binGap = binGreenClearance(plan.bounds);

  // The errand is a **round trip** — out to the bin and back to the front door
  // — so the target length is split between the two legs.
  //
  // Measured against the **playable** rectangle, not the whole grid. The
  // surround is scenery, and scaling the errand by a diagonal that includes it
  // would stretch every walk by however much town happens to be drawn beyond
  // the boundary — which is not a difficulty decision anyone made.
  const target = budget.journeyTarget * Math.hypot(plan.bounds.w, plan.bounds.h);

  let best: Landmarks | null = null;
  let bestCost = Infinity;
  for (const home of homeSpots) {
    const homeRect: Rect = box(home, {
      w: OBJECTIVE.HOME.WIDTH,
      h: OBJECTIVE.HOME.HEIGHT,
    });
    for (const bin of binSpots) {
      // Anything closer than the separation is disqualified outright rather
      // than merely penalised: a bin twenty units from the front door isn't a
      // slightly worse level, it's a level with no walk in it.
      if (dist(home, bin) < LANDMARK_SEPARATION) continue;

      for (const green of greenSpots) {
        const greenRect = box(green, GREEN_FOOTPRINT);
        // Edge to edge, which also covers the overlap case that a centre-to-
        // centre rule misses: two rects 150 apart on the diagonal still overlap,
        // because the separation is spent across both axes rather than either.
        // Grass laid under the home pad is two textured slabs on one patch,
        // z-fighting the whole run.
        if (rectGap(greenRect, homeRect) < greenGap) continue;
        // And clear of the bin, which is the same rule pointed the other way:
        // the bag is filled on the grass, so a bin within sight of it deletes
        // the errand. See `binGreenClearance`.
        if (rectGap(greenRect, box(bin, BIN_FOOTPRINT)) < binGap) continue;

        // Out and back, plus what the grass adds to it, weighted lightly — the
        // player picks when to make that detour, so it shapes the walk without
        // dominating the score the way the errand does.
        //
        // What is scored is the **detour**: how much longer home → green → bin
        // is than going straight to the bin. Scoring the raw distance to the
        // green instead (which is what this did) rewards a green as close to
        // the door as the rules allow, and that is how every level ended up
        // with a park in the front garden. Penalising the detour instead pulls
        // the green out along the way to the bin, which is where a park you
        // stop at on a walk actually is.
        const errand = dist(home, bin) * 2;
        const detour = dist(home, green) + dist(green, bin) - dist(home, bin);
        const cost = Math.abs(errand - target) + detour * 0.25;
        if (cost < bestCost) {
          bestCost = cost;
          best = {
            bin,
            home: homeRect,
            // The walk begins on the doormat — in front of the house, not
            // inside it. `Game` spawns the dog at heel beside this, which the
            // front garden is comfortably wide enough for.
            start: homeDoorstep(homeRect),
            greens: [greenRect],
            reserved: [
              inflateRect(homeRect, LANDMARK_MARGIN),
              box(bin, BIN_FOOTPRINT),
              inflateRect(greenRect, LANDMARK_MARGIN),
            ],
          };
        }
      }
    }
  }
  if (!best) return null;

  // The town's other greens, claimed now rather than scavenged later. See
  // `Landmarks.greens`.
  for (const green of extraGreens(
    rng,
    best,
    greenSpots,
    greenGap,
    binGap,
    rng.int(...budget.parks) - 1,
  )) {
    best.greens.push(green);
    best.reserved.push(inflateRect(green, LANDMARK_MARGIN));
  }
  return best;
}

/**
 * More greens, from the same shortlist the first one was chosen out of.
 *
 * Held to the same distance from the front door as the green the search chose:
 * the rule is about the *town*, not about that one park, and a spare green laid
 * behind the house would put the walk back on the doorstep by another route.
 *
 * Taken in shuffled order rather than scored. The first green is load-bearing —
 * it decides how far the detour is — but a second one is somewhere else to walk
 * the dog, and any corner of the town that has room for it will do.
 */
function extraGreens(
  rng: Rng,
  base: Landmarks,
  spots: readonly Point[],
  greenGap: number,
  binGap: number,
  want: number,
): Rect[] {
  if (want <= 0) return [];
  const out: Rect[] = [];
  for (const at of rng.shuffle([...spots])) {
    if (out.length >= want) break;
    const rect = box(at, GREEN_FOOTPRINT);
    if (rectGap(rect, base.home) < greenGap) continue;
    // Held off the errand bin by the same clearance the first green is, and for
    // the same reason: a spare green laid across the road from the bin would
    // put the bag within sight of one by another route, exactly as a spare
    // green behind the house would put the walk back on the doorstep.
    if (rectGap(rect, box(base.bin, BIN_FOOTPRINT)) < binGap) continue;
    // Clear of everything already claimed — including the bin's drop zone, which
    // is reserved as its own rect, and of the other greens by a wide enough
    // margin that two of them can't read as one park with a path through it.
    const claimed = inflateRect(rect, LANDMARK_MARGIN);
    if (base.reserved.some((r) => rectsOverlap(claimed, r))) continue;
    if (out.some((g) => rectGap(g, rect) < GREEN_SPACING)) continue;
    out.push(rect);
  }
  return out;
}

/** How much town there has to be between two greens for them to be two greens. */
const GREEN_SPACING = 150;

const box = (at: Point, size: { w: number; h: number }): Rect => ({
  x: at.x - size.w / 2,
  y: at.y - size.h / 2,
  w: size.w,
  h: size.h,
});

/**
 * Candidate centres for home: placed flush against a paved street frontage of a block
 * (preferring South frontages so the cottage stands at the back of the plot facing South
 * onto the street/pavement).
 */
function shortlistHomeSpots(
  rng: Rng,
  plan: StreetPlan,
  blocks: readonly Rect[],
  w: number,
  h: number,
  per: number,
): Point[] {
  const footways = footwayBands(plan.world, plan.roads, ROADS.WIDTH, PAVEMENT.WIDTH);
  const needW = w + LANDMARK_MARGIN * 2;
  const needH = h + LANDMARK_MARGIN * 2;
  const out: Point[] = [];

  const validBlocks = blocks.filter((b) => b.w >= needW && b.h >= needH);
  const southPavedBlocks = validBlocks.filter((b) => {
    const gaps = faceGaps(b, footways);
    return gaps.south <= 10;
  });

  const pool = southPavedBlocks.length > 0 ? southPavedBlocks : validBlocks;

  for (const block of pool) {
    const cy = block.y + block.h - h / 2;
    for (let i = 0; i < per; i++) {
      out.push({
        x: rng.float(block.x + needW / 2, block.x + block.w - needW / 2),
        y: cy,
      });
    }
  }

  return rng.shuffle(out).slice(0, 24);
}

/** The four faces of a block, south first — the one this camera really shows. */
const FACES: readonly Facing[] = ['south', 'north', 'east', 'west'];

/**
 * How far a block edge may be off the pavement and still count as a frontage.
 * The same slack `town.ts` allows its plots, and for the same reason: a
 * generated block's edges land on fractional coordinates.
 */
const PAVED_FACE_TOLERANCE = 2;

/**
 * Candidate bin positions: **against a paved frontage**, set back a pace from
 * the kerb.
 *
 * This replaced a jittered scatter over the whole block interior, which is
 * where "a bin in the middle of nowhere" came from — the interior of a block is
 * back gardens and side passages, and a municipal bin standing in one is an
 * object with no reason to be there, found only by walking into it. Against a
 * frontage it is on the route the player is already walking, which is the only
 * place street furniture is ever found.
 */
function kerbsideSpots(
  rng: Rng,
  blocks: readonly Rect[],
  footways: readonly Rect[],
  per: number,
): Point[] {
  const out: Point[] = [];
  for (const block of blocks) {
    const gaps = faceGaps(block, footways);
    for (const facing of FACES) {
      if (gaps[facing] > PAVED_FACE_TOLERANCE) continue;
      // Kept off the corners. A bin in the angle where two frontages meet is
      // round the corner from both of them, and behind whatever stands on it.
      const corner = BIN_SETBACK * 2;
      const face = facing === 'north' || facing === 'south' ? block.w : block.h;
      const run = face - corner * 2;
      if (run <= 0) continue;
      for (let i = 0; i < per; i++) {
        out.push(faceSpot(block, facing, rng.float(corner, corner + run), BIN_SETBACK));
      }
    }
  }
  // Capped like every other shortlist here: the search over these is cubic.
  return rng.shuffle(out).slice(0, 24);
}

/** A point `along` one face of a block, set `inset` back into it. */
function faceSpot(block: Rect, facing: Facing, along: number, inset: number): Point {
  switch (facing) {
    case 'south':
      return { x: block.x + along, y: block.y + block.h - inset };
    case 'north':
      return { x: block.x + along, y: block.y + inset };
    case 'east':
      return { x: block.x + block.w - inset, y: block.y + along };
    default:
      return { x: block.x + inset, y: block.y + along };
  }
}

/**
 * Candidate centres inside the blocks with room for a `w × h` footprint plus
 * its margin — a jittered grid, so two candidates in one big block are actually
 * in different parts of it.
 */
function shortlist(rng: Rng, blocks: readonly Rect[], w: number, h: number, per: number): Point[] {
  const need = { w: w + LANDMARK_MARGIN * 2, h: h + LANDMARK_MARGIN * 2 };
  const out: Point[] = [];

  for (const block of blocks) {
    if (block.w < need.w || block.h < need.h) continue;
    for (let i = 0; i < per; i++) {
      out.push({
        x: rng.float(block.x + need.w / 2, block.x + block.w - need.w / 2),
        y: rng.float(block.y + need.h / 2, block.y + block.h - need.h / 2),
      });
    }
  }
  return rng.shuffle(out).slice(0, 24);
}

const dist = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * Every bin in the town: the errand's, plus the rest.
 *
 * ## Why a town has more than one
 *
 * A level used to have exactly one bin, and a player carrying a bag had to find
 * that one object on a map of terraces and back lanes. That is a search, and it
 * is a different game from the one being played on the way to it. Real towns
 * solve this by having bins all over, and so does this one now — several, in
 * the places a bin is actually bolted down.
 *
 * ## Why they come after the town is built
 *
 * The errand bin has to be chosen before the houses, because the whole point of
 * it is to fix how long the walk is and the town is then built around it (see
 * `placeLandmarks`). The extras have the opposite requirement: they have to be
 * kept **off** the greens, and the greens do not exist until the plots have
 * been laid and `placeParks` has seen what ground is left. So they are placed
 * last, against the finished town, and checked against the real plots and the
 * real parks rather than against a reservation.
 *
 * ## The three rules
 *
 * 1. **Where.** Against a paved frontage, never loose in the middle of a block:
 *    `frontageGaps` is the whole of that judgement.
 * 2. **Not by the grass.** `binGreenClearance` from every green. This used to
 *    be the exact opposite — a `parkSideSpots` candidate set and a bonus that
 *    *preferred* the apron beside a park, so the bag was reliably filled within
 *    sight of somewhere to put it. Read the note on the constant; it is the
 *    difference between an errand and three paces.
 * 3. **How near home.** No nearer than the errand bin, give or take
 *    (`EXTRA_BIN_HOME_FLOOR`). An extra bin is meant to make the town legible,
 *    not to move the finish line — and the nearest bin is the one a player
 *    walks to, so without this rule a spare one round the corner from the front
 *    door would turn a rated errand into a stroll to the end of the street.
 */
export function placeBins(
  plan: StreetPlan,
  plots: readonly Rect[],
  parks: readonly Rect[],
  landmarks: Landmarks,
  want: number,
): Point[] {
  const bins: Point[] = [landmarks.bin];
  const body = OBJECTIVE.BIN.SIZE / 2;
  const binGap = binGreenClearance(plan.bounds);
  const inPlay = plan.blocks.filter((b) => rectContains(plan.bounds, b));
  const footways = footwayBands(plan.world, plan.roads, ROADS.WIDTH, PAVEMENT.WIDTH);
  const roads = carriagewayRects(plan.world, plan.roads, ROADS.WIDTH);
  const homeCentre = { x: landmarks.home.x + landmarks.home.w / 2, y: landmarks.home.y + landmarks.home.h / 2 };

  const fits = (at: Point): boolean => {
    // Room for the bin *and* its drop zone inside the walk: a bin the walker is
    // clamped away from is a bin they can stand beside and not use.
    if (!pointInRect(at.x, at.y, inflateRect(plan.bounds, -OBJECTIVE.BIN_RADIUS))) return false;
    if (pointInAnyRect(at.x, at.y, footways, body)) return false;
    if (pointInAnyRect(at.x, at.y, roads, body)) return false;
    // Well clear of the grass — not merely off it. A bin standing where the dog
    // has just been is the back half of the errand deleted; see
    // `binGreenClearance`.
    if (parks.some((p) => rectGap(p, pointRect(at.x, at.y)) < binGap)) return false;
    if (pointInAnyRect(at.x, at.y, plots, body + 8)) return false;
    if (pointInRect(at.x, at.y, inflateRect(landmarks.home, LANDMARK_MARGIN))) return false;
    if (dist(at, homeCentre) < LANDMARK_SEPARATION) return false;
    // Never nearer the door than the bin the errand was measured to — on either
    // leg of it, since the walk out starts at the doorstep and ends at the pad.
    if (dist(at, homeCentre) < dist(landmarks.bin, homeCentre) * EXTRA_BIN_HOME_FLOOR) return false;
    if (dist(at, landmarks.start) < dist(landmarks.bin, landmarks.start) * EXTRA_BIN_HOME_FLOOR) {
      return false;
    }
    return true;
  };

  const candidates = inPlay
    .flatMap((block) => frontageGaps(block, plots, footways))
    .filter(fits);

  // Farthest-first. Taking them in shuffled order instead reliably put two bins
  // on the same street: what makes a second bin worth having is the ground it
  // covers that the first doesn't, so that is what is maximised.
  //
  // There used to be a thumb on the scale here for spots beside a green, worth
  // about a street's distance. It pulled in precisely the wrong direction —
  // see rule 2 above — and the candidates it was weighting are gone with it.
  while (bins.length < want) {
    let best: Point | undefined;
    let bestGap = BIN_SEPARATION;
    for (const at of candidates) {
      const gap = Math.min(...bins.map((b) => dist(at, b)));
      if (gap > bestGap) {
        bestGap = gap;
        best = at;
      }
    }
    if (!best) break; // the town simply has nowhere else to put one
    bins.push(best);
  }
  return bins;
}

/**
 * Spots on the paved apron a terrace leaves along a block's paved frontages.
 *
 * The post-town version of `kerbsideSpots`, and it has to be a different
 * function rather than the same one filtered: by the time the extra bins are
 * placed the frontage is a row of cottages built flush to it, so a point picked
 * anywhere along that edge is almost always inside somebody's front wall.
 * Sampling harder does not fix that — what's wanted is the **gaps**, the side
 * passages and undeveloped slots between the houses, which is exactly what
 * subtracting the plots from the block leaves behind.
 */
function frontageGaps(block: Rect, plots: readonly Rect[], footways: readonly Rect[]): Point[] {
  const gaps = faceGaps(block, footways);
  const out: Point[] = [];
  // Wide enough to stand a bin in with room to walk past it.
  for (const patch of freeRects(block, plots, OBJECTIVE.BIN.SIZE + 20)) {
    for (const facing of FACES) {
      if (gaps[facing] > PAVED_FACE_TOLERANCE) continue;
      if (!reachesFace(block, patch, facing)) continue;
      const across = facing === 'north' || facing === 'south' ? patch.w : patch.h;
      const from = facing === 'north' || facing === 'south' ? patch.x - block.x : patch.y - block.y;
      // Along the street end of the gap, not only at the middle of it.
      //
      // One spot per patch was enough while `parkSideSpots` was also on offer:
      // every green contributed four candidates that were legal by
      // construction, so the pool never ran dry. Holding bins clear of the
      // greens (see `BIN_GREEN_CLEARANCE`) took all of those away at once, and
      // a third of towns fell back to shipping the single bin that `placeBins`
      // exists to avoid. The gaps themselves are the pool now, so they are
      // sampled at the density a wide one deserves rather than once each.
      //
      // A wide gap genuinely holds more than one bin; `BIN_SEPARATION` is what
      // stops two of them being chosen off the same frontage, so sampling
      // harder costs nothing but candidates.
      for (const at of alongFace(across)) {
        out.push(faceSpot(block, facing, from + at, BIN_SETBACK));
      }
    }
  }
  return out;
}

/**
 * Offsets along a frontage gap to offer a bin, given how wide it is.
 *
 * The middle always; the quarters as well once the gap is wide enough that the
 * three are meaningfully different places rather than three names for the same
 * corner. `BIN_SEPARATION` is the yardstick for "meaningfully different" — two
 * spots closer together than that can never both be taken anyway.
 */
function alongFace(across: number): number[] {
  if (across < BIN_SEPARATION) return [across / 2];
  return [across * 0.25, across / 2, across * 0.75];
}

/** Whether a patch of free ground reaches the given face of its block. */
function reachesFace(block: Rect, patch: Rect, facing: Facing): boolean {
  const slack = PAVED_FACE_TOLERANCE;
  switch (facing) {
    case 'south':
      return patch.y + patch.h >= block.y + block.h - slack;
    case 'north':
      return patch.y <= block.y + slack;
    case 'east':
      return patch.x + patch.w >= block.x + block.w - slack;
    default:
      return patch.x <= block.x + slack;
  }
}

/**
 * Plant the trees.
 *
 * Candidates are scattered over the free ground and the parks and then filtered
 * hard, because a tree is the one piece of scenery that can genuinely break the
 * level: the trunks are **solid**, and the canopies are drawn ~30 units north
 * of them at this camera tilt, so a badly placed one either walls off a route
 * or hides the walker under a bush.
 *
 * Every rule here has an equivalent assertion in `validate.ts`. That is
 * deliberate duplication — the placer's job is to produce a plausible tree and
 * the validator's is to refuse to believe it, and a rule enforced in only one
 * of the two is a rule that silently stops applying the day the placer changes.
 */
export function plantTrees(
  rng: Rng,
  plan: StreetPlan,
  plots: readonly Rect[],
  parks: readonly Rect[],
  landmarks: Landmarks,
  /**
   * Every bin, not just the errand's. Only the errand bin's ground is in
   * `landmarks.reserved` — the rest are placed after the plots — so they are
   * passed in and kept clear here by the same rule.
   */
  bins: readonly Point[] = [landmarks.bin],
): TreeSpec[] {
  const bands = footwayBands(plan.world, plan.roads, ROADS.WIDTH, PAVEMENT.WIDTH).map((b) =>
    inflateRect(b, FOOTWAY_MARGIN),
  );
  const roads = carriagewayRects(plan.world, plan.roads, ROADS.WIDTH);
  const world: Rect = { x: 0, y: 0, w: plan.world.width, h: plan.world.height };
  const dogSpawn = { x: landmarks.start.x + DOG_OFFSET, y: landmarks.start.y };

  // Parks first, so the greens are planted before the back yards compete for
  // the same tree budget — a park with nothing growing in it is the one place
  // a missing tree is really obvious. They also get a tighter grid: a park is
  // open ground with its whole perimeter onto the block, so trees in one can't
  // seal a route the way a row down a side passage can.
  const ground = [
    ...parks.map((patch) => ({ patch, spacing: 62 })),
    ...plan.blocks
      // Not the far surround. Every candidate here is checked against every tree
      // already planted, so this sweep is the most expensive stage of the whole
      // generator — and out past the inner ring it was buying trees standing in
      // fog thick enough to hide them completely. See `worthPlanting`.
      .filter((block) => worthPlanting(block, plan.bounds))
      // Small enough to catch a back yard with room for one tree in it. A
      // higher floor here is what left a whole small town treeless.
      .flatMap((block) => freeRects(block, [...plots, ...parks], 56))
      .map((patch) => ({ patch, spacing: 84 })),
  ];

  const kinds: TreeKind[] = ['round', 'tall', 'bushy'];
  const out: TreeSpec[] = [];
  let nextKind = rng.int(0, kinds.length - 1);

  for (const { patch, spacing } of ground) {
    // Spaced wider than a canopy is across, off the grass. Trunks are solid, so
    // a tight row of them is a fence: at 8 units of grid resolution, trees much
    // closer than that leave a gap the walkability check reads as sealed —
    // which is a level thrown away, or worse, a route that only *looks* open.
    for (const at of gridPoints(rng, patch, spacing)) {
      // Try each silhouette in turn from a rotating start, so the mix stays
      // even and a neighbour clash can be resolved by taking a different shape
      // rather than by abandoning the position.
      for (let attempt = 0; attempt < kinds.length; attempt++) {
        const kind = kinds[(nextKind + attempt) % kinds.length];
        const scale = Math.round(rng.float(0.7, 1) * 20) / 20; // quantised, so
        const tree: TreeSpec = { x: at.x, y: at.y, scale, kind }; // "identical" is decidable
        const ctx = { bands, roads, world, plots, landmarks, bins, dogSpawn, planted: out };
        if (!treeFits(tree, ctx)) continue;
        out.push(tree);
        nextKind = (nextKind + attempt + 1) % kinds.length;
        break;
      }
    }
  }
  return out;
}

/** Canopy envelope radius for a tree, after its scale and kind multipliers. */
export function treeEnvelope(t: TreeSpec): number {
  return TREES.CANOPY_RADIUS * t.scale * TREES.KINDS[t.kind].canopy;
}

/** Height of the top of the trunk, which is how far north the canopy is drawn. */
export function treeTrunkTop(t: TreeSpec): number {
  return TREES.TRUNK_HEIGHT * t.scale * TREES.KINDS[t.kind].trunk;
}

interface TreeContext {
  bands: readonly Rect[];
  roads: readonly Rect[];
  world: Rect;
  plots: readonly Rect[];
  landmarks: Landmarks;
  bins: readonly Point[];
  dogSpawn: Point;
  planted: readonly TreeSpec[];
}

/** Every rule about where a tree may stand, in one place. */
function treeFits(t: TreeSpec, ctx: TreeContext): boolean {
  const envelope = treeEnvelope(t);
  const ground = canopyRect(t.x, t.y, envelope);
  const drawn = apparentCanopyRect(t.x, t.y, envelope, treeTrunkTop(t), t.kind, CAMERA.TILT_DEG);

  // Inside the map, off the tarmac, out of the houses.
  if (
    ground.x < ctx.world.x ||
    ground.y < ctx.world.y ||
    ground.x + ground.w > ctx.world.x + ctx.world.w ||
    ground.y + ground.h > ctx.world.y + ctx.world.h
  ) {
    return false;
  }
  if (ctx.roads.some((r) => rectsOverlap(ground, r))) return false;
  if (ctx.plots.some((p) => rectsOverlap(ground, p))) return false;
  // Both the footprint and where it's actually drawn: the second is the strict
  // one, and the only one that catches foliage hanging over the pavement.
  if (ctx.bands.some((b) => rectsOverlap(ground, b) || rectsOverlap(drawn, b))) return false;

  // Nothing planted on the ground the landmarks reserved. That one rule covers
  // the bin's drop zone, the home pad and the opening, and covers them
  // *generously* — a trunk is solid, so a tree just outside the walker's start
  // is a bollard they have to squeeze past on frame one.
  const { home, start, reserved } = ctx.landmarks;
  if (reserved.some((r) => rectsOverlap(ground, r))) return false;
  for (const bin of ctx.bins) {
    if (Math.hypot(t.x - bin.x, t.y - bin.y) <= OBJECTIVE.BIN_RADIUS + TREES.TRUNK_RADIUS) {
      return false;
    }
  }
  if (rectsOverlap(ground, home)) return false;
  const trunk = TREES.TRUNK_RADIUS * t.scale;
  if (Math.hypot(t.x - start.x, t.y - start.y) <= PLAYER.RADIUS + trunk + 8) return false;
  if (Math.hypot(t.x - ctx.dogSpawn.x, t.y - ctx.dogSpawn.y) <= DOG.RADIUS + trunk + 8) return false;

  for (const other of ctx.planted) {
    const gap = Math.hypot(t.x - other.x, t.y - other.y);
    // Trunks apart…
    if (gap <= TREES.TRUNK_RADIUS * (t.scale + other.scale) + 6) return false;
    // …and no identical twin next door, which is what reads as copy-paste.
    if (gap < 120 && other.kind === t.kind && other.scale === t.scale) return false;
  }
  return true;
}

/**
 * Positions on a jittered grid across a patch.
 *
 * A grid rather than pure random scatter, for the reason `yards.ts` gives about
 * bushes: at these densities random placement reliably clumps three trees in a
 * heap and leaves a bare patch beside them.
 */
function gridPoints(rng: Rng, patch: Rect, spacing: number, tries = 2): Point[] {
  const inset = TREES.CANOPY_RADIUS + 4;
  const w = patch.w - inset * 2;
  const h = patch.h - inset * 2;
  if (w <= 0 || h <= 0) return [];

  const cols = Math.max(1, Math.round(w / spacing));
  const rows = Math.max(1, Math.round(h / spacing));
  const cw = w / cols;
  const ch = h / rows;

  const out: Point[] = [];
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      // Several offers per cell rather than one. A small park is a single cell,
      // so one offer means one throw of the dice for the whole green — and a
      // park with nothing growing in it is the most obvious kind of bare level.
      // Once a tree is planted the rest of its cell's offers fail the
      // trunk-spacing rule anyway, so this thickens sparse ground without
      // crowding good ground.
      for (let k = 0; k < tries; k++) {
        out.push({
          x: patch.x + inset + (i + 0.5) * cw + rng.float(-0.34, 0.34) * cw,
          y: patch.y + inset + (j + 0.5) * ch + rng.float(-0.34, 0.34) * ch,
        });
      }
    }
  }
  return out;
}
